import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgbModal,
  NgbPaginationModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { MasterInformationService } from '../../../Services/master-information.service';
import { apiEndPoints } from '../../../api/apiEndPoint';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-containers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    ToastrModule,
    NgbPaginationModule,
  ],
  templateUrl: './containers.component.html',
  styleUrls: ['./containers.component.css'],
})
export class ContainersComponent implements OnInit {
  createContainerForm: FormGroup;
  allContainerList: any;

  // ---------------------- Pagination ---------------------- //
  page = 1;
  pageSize = 10;
  originalTableData: any;
  cityList: any;
  changeCityId: any;
  pageSizeOptions = [5, 10, 25, 50, 100];

  constructor(
    public modalService: NgbModal,
    public fb: FormBuilder,
    public masterService: MasterInformationService,
    private toastr: ToastrService
  ) {
    // ---------------------------------- Create container form ---------------------------------- //

    this.createContainerForm = this.fb.group({
      containerName: ['', Validators.required],
      containerCapacity: ['', Validators.required],
      volume: ['', Validators.required],
      containerSize: ['', Validators.required],
      length: ['', Validators.required],
      width: ['', Validators.required],
      height: ['', Validators.required],
      containerPounds: ['', Validators.required],
      cityId: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.getInitialContainerList();
    this.getCity();
  }

  // Dynamic filter fields
  filterContainerName: string = '';
  filterContainerCapacity: string = '';
  filterByName: boolean = false; // Track filter visibility for container name
  filterByCapacity: boolean = false; // Track filter visibility for container capacity

  uniqueContainerNames: string[] = [];
  uniqueContainerCapacities: string[] = [];

  getUniqueContainerNames(): string[] {
    return Array.from(
      new Set(
        this.allContainerList.map((container: any) => container.containerName)
      )
    );
  }

  getUniqueContainerCapacities(): string[] {
    return Array.from(
      new Set(
        this.allContainerList.map((container: any) => container.containerInTons)
      )
    );
  }

  openCreateContainerModal(content: any) {
    this.createContainerForm.reset();
    this.modalService.open(content, {
      centered: true,
      backdrop: 'static',
      size: 'lg',
    });
  }

  // ---------------------------------- Add Container API ---------------------------------- //

  addContainer(modal: any) {
    if (this.createContainerForm.valid) {
      const { length, width, height } = this.createContainerForm.value;
      const containerDimension = `${length}cm x ${width}cm x ${height}cm`;
      const tons = this.createContainerForm.value.containerCapacity;
      const capacityInTon = `${tons}`;
      const reqBody = {
        cityId: this.createContainerForm.value.cityId,
        containerName: this.createContainerForm.value.containerName,
        containerInTons: capacityInTon,
        containerExactCapacity: this.createContainerForm.value.volume,
        containerSize: this.createContainerForm.value.containerSize,
        containerDimensions: containerDimension,
        containerInPounds: this.createContainerForm.value.containerPounds,
      };
      console.log(reqBody);
      this.masterService
        .postContainerApi(reqBody, apiEndPoints.addSAContainer)
        .subscribe({
          next: (response: any) => {
            modal.close();
            this.toastr.success('Container Added Successfully', 'Success', {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            });
            this.createContainerForm.reset();
            this.getInitialContainerList();
          },
          error: (error) => {
            this.toastr.error('Error adding container', 'Error', {
              timeOut: 3000,
              positionClass: 'toast-top-right',
              progressBar: true,
            });
          },
        });
    } else {
      this.createContainerForm.markAllAsTouched();
      this.toastr.info('Make sure you complete every field.', 'Info', {
        timeOut: 3000,
        positionClass: 'toast-top-right',
        progressBar: true,
      });
    }
  }

  // ---------------------------------- Get All Container List API ---------------------------------- //

  getContainerList() {
    this.masterService
      .getSAAllContainer(apiEndPoints.containerListSA, this.changeCityId)
      .subscribe({
        next: (response: any) => {
          this.allContainerList = response.body.details;
          this.originalTableData = [...this.allContainerList];
          this.uniqueContainerNames = this.getUniqueContainerNames();
          this.uniqueContainerCapacities = this.getUniqueContainerCapacities();
        },
        error: (error) => {
          this.toastr.error('Error fetching container list.', 'Error', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          });
        },
      });
  }

  getInitialContainerList() {
    this.masterService
      .getAllContainer(apiEndPoints.initialContainerList)
      .subscribe({
        next: (response: any) => {
          this.allContainerList = response.body.details;
          this.originalTableData = [...this.allContainerList];
          this.uniqueContainerNames = this.getUniqueContainerNames();
          this.uniqueContainerCapacities = this.getUniqueContainerCapacities();
        },
        error: (error) => {
          this.toastr.error('Error fetching container list.', 'Error', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          });
        },
      });
  }

  // ----------------------------- Pagination ----------------------------- //

  get pagedData() {
    const start = (this.page - 1) * this.pageSize;
    const end = this.page * this.pageSize;
    return this.allContainerList?.slice(start, end);
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onPageSizeChange(): void {
    this.page = 1;
  }

  // ------------------------------- Search Filter ------------------------------- //

  search(event: any) {
    const searchText = event.target.value.toLowerCase();
    console.log(searchText);

    if (!this.originalTableData) {
      this.originalTableData = [...this.allContainerList];
    }

    if (searchText) {
      console.log(searchText);
      this.allContainerList = this.originalTableData.filter(
        (item: any) =>
          (item.containerName?.toLowerCase().includes(searchText) ||
            item.containerCapacity?.toLowerCase().includes(searchText)) ??
          false
      );
    } else {
      this.allContainerList = [...this.originalTableData];
    }
  }

  // ----------------------------------- Get All City List API ----------------------------------- //

  getCity() {
    this.masterService.getCityList(apiEndPoints.cityList).subscribe({
      next: (response: any) => {
        this.cityList = response.body.details;
      },
      error: (error) => {
        this.toastr.error(
          'An unexpected error occurred. Please try again later.',
          'Error',
          {
            timeOut: 3000,
            positionClass: 'toast-top-right',
            progressBar: true,
          }
        );
      },
    });
  }

  // ----------------------------------- City Change Dropdown ----------------------------------- //

  onCityChange(event: Event) {
    const selectedCityId = (event.target as HTMLSelectElement).value;
    console.log(selectedCityId);
  }

  onFilterCityChange(event: Event) {
    const selectedCityId = (event.target as HTMLSelectElement).value;
    if (selectedCityId == 'all') {
      this.getInitialContainerList();
      return;
    }
    this.changeCityId = selectedCityId;
    this.getContainerList();
  }

  // ------------------------------- Export to Excel ------------------------------- //
  exportToExcel(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.allContainerList);
    const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
    XLSX.writeFile(wb, 'ContainerList.xlsx');
  }

  // Function to toggle filter input visibility
  toggleFilter(filterType: string): void {
    if (filterType === 'name') {
      this.filterByName = true;
      this.filterByCapacity = false;
    } else if (filterType === 'capacity') {
      this.filterByCapacity = true;
      this.filterByName = false;
    }
  }

  // Apply the filter to the list of cities
  applyFilters(): void {
  
  }

  // Placeholder for printing the grid
  printGrid(): void {
    console.log('Print triggered');
  }

  // Clear the grid
  clearGrid(): void {
    // this.filteredCities = [];
    console.log('Grid cleared');
  }

  clearFilter() {
    this.allContainerList = this.originalTableData;
  }
}
